import { useCallback } from 'react';

import { useConfirmation } from './useConfirmation';

import type { User } from '@/types/directory/User';
import type { ReviewEvaluatorSelection } from '@/types/review/ReviewAudience';

import { hasSubmittedAnswers, type Reviewee } from '@/utils/reviewEvaluatorSelection';

interface RemoveReviewerParams {
    revieweeId: string;
    reviewerId: string;
}

interface ConfirmSubmittedRemovalParams {
    reviewees: Reviewee[];
    reviewEvaluatorSelection?: ReviewEvaluatorSelection;
    removeReviewer: (params: RemoveReviewerParams) => void;
}

/**
 * Removing a respondent who already answered deletes their answers once saved: `remove` asks first for them, and
 * removes anyone else at once. `pending` is the respondent the question is about, until `answer` closes it.
 */
export const useConfirmSubmittedRemoval = ({
    reviewees,
    reviewEvaluatorSelection,
    removeReviewer,
}: ConfirmSubmittedRemovalParams) => {
    const { pending, ask, answer } = useConfirmation<User>();

    const remove = useCallback(
        async ({ revieweeId, reviewerId }: RemoveReviewerParams) => {
            const reviewee = reviewees.find(({ id }) => id === revieweeId);
            const reviewer = reviewee?.reviewers.find(({ id }) => id === reviewerId);
            const hasSubmitted =
                reviewee !== undefined &&
                hasSubmittedAnswers({
                    reviewEvaluatorSelection,
                    reviewAudienceAssignmentId: reviewee.reviewAudienceAssignmentId,
                    reviewerId,
                });

            if (reviewer && hasSubmitted && !(await ask(reviewer))) {
                return;
            }

            removeReviewer({ revieweeId, reviewerId });
        },
        [reviewees, reviewEvaluatorSelection, removeReviewer, ask],
    );

    return { pending, remove, answer };
};
